export default class ProduitsEcoOrganismeController {
	static $inject = ['notification', '$translate', 'ModalService', 'ProduitsEcoOrganismeService', '$scope'];

	constructor(notification, $translate, ModalService, ProduitsEcoOrganismeService, $scope) {
		this.notification = notification;
		this.$translate = $translate;
		this.ModalService = ModalService;
		this.ProduitsEcoOrganismeService = ProduitsEcoOrganismeService;
		this.$scope = $scope;

		this.searchText = '';
		this.filterProduit = this.filterProduit.bind(this);
	}

	$onInit() {
		this.initializeData();
		this.watchAccordionOpen();
	}

	initializeData() {
		this.societe = this.societe || {};
		this.societe.entete = this.societe.entete || {};
		this.produits = [];
		this.newProduit = {};
		this.loading = false;
		this.hasLoadedOnce = false;
	}

	watchAccordionOpen() {
		this.$scope.$watch(
			() => this.isOpen,
			(newValue, oldValue) => {
				if (newValue && !oldValue && !this.hasLoadedOnce) {
					this.hasLoadedOnce = true;
					this.loadProduits();
				}
			}
		);
	}

	// ============ CHARGEMENT DES DONNÉES ============

	async loadProduits() {
		if (!this.societe.entete.id) {
			return;
		}

		this.startLoading();
		try {
			this.produits = await this.ProduitsEcoOrganismeService.getProduits(this.societe.entete.id);
		} catch (error) {
			this.notification.error('SOCIETES.PRODUITS_ECO_ORGANISME.LOAD_PRODUITS_ERROR');
		} finally {
			this.stopLoading();
		}
	}

	// ============ FILTRAGE ET RECHERCHE ============

	removeAccents(str) {
		if (!str) return '';
		return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
	}

	filterProduit(produit) {
		if (!this.searchText || this.searchText.length < 2) {
			return true;
		}

		const searchNormalized = this.removeAccents(this.searchText.toLowerCase());
		const codeNormalized = this.removeAccents((produit.codeDechet || '').toLowerCase());
		const libelleNormalized = this.removeAccents((produit.denominationDechet || '').toLowerCase());

		return codeNormalized.includes(searchNormalized) || libelleNormalized.includes(searchNormalized);
	}

	// ============ VALIDATION ============

	validateCodeFormat(code) {
		return /^[0-9]{1,8}$/.test(code);
	}

	async checkCodeUniqueness(code, excludeId = null) {
		try {
			const codeExists = await this.ProduitsEcoOrganismeService.checkCodeExists(this.societe.entete.id, code, excludeId);

			if (codeExists) {
				this.notification.error('SOCIETES.PRODUITS_ECO_ORGANISME.CODE_UNIQUE_ERROR');
				return false;
			}
			return true;
		} catch (error) {
			this.notification.error('SOCIETES.PRODUITS_ECO_ORGANISME.CODE_UNIQUE_VALIDATION_ERROR');
			return false;
		}
	}

	// ============ OPÉRATIONS CRUD ============

	async addProduit() {
		if (!this.validateCodeFormat(this.newProduit.codeDechet)) {
			this.notification.error('SOCIETES.PRODUITS_ECO_ORGANISME.CODE_FORMAT_ERROR');
			return;
		}

		if (!this.newProduit.codeDechet || !this.newProduit.denominationDechet) {
			this.notification.error('SOCIETES.PRODUITS_ECO_ORGANISME.FORM_INVALID');
			return;
		}

		// Vérifier l'unicité du code
		const isUnique = await this.checkCodeUniqueness(this.newProduit.codeDechet);
		if (!isUnique) {
			return;
		}

		this.startLoading();
		try {
			await this.ProduitsEcoOrganismeService.createProduit(this.societe.entete.id, {
				codeDechet: this.newProduit.codeDechet,
				denominationDechet: this.newProduit.denominationDechet
			});

			this.newProduit = {};
			await this.loadProduits();
			this.notification.success('SOCIETES.PRODUITS_ECO_ORGANISME.ADDED');
		} catch (error) {
			this.notification.error('SOCIETES.PRODUITS_ECO_ORGANISME.ADD_FAILED');
		} finally {
			this.stopLoading();
		}
	}

	editProduit(produit) {
		produit._originalCodeDechet = produit.codeDechet;
		produit._originalDenominationDechet = produit.denominationDechet;
		produit.editMode = true;
	}

	async saveProduit(produit) {
		if (!produit.codeDechet || !produit.denominationDechet) {
			this.notification.error('SOCIETES.PRODUITS_ECO_ORGANISME.FORM_INVALID');
			return;
		}

		if (!this.validateCodeFormat(produit.codeDechet)) {
			this.notification.error('SOCIETES.PRODUITS_ECO_ORGANISME.CODE_FORMAT_ERROR');
			return;
		}

		// Vérifier l'unicité du code
		const isUnique = await this.checkCodeUniqueness(produit.codeDechet, produit.id);
		if (!isUnique) {
			return;
		}

		this.startLoading();
		try {
			await this.ProduitsEcoOrganismeService.updateProduit(this.societe.entete.id, produit.id, {
				codeDechet: produit.codeDechet,
				denominationDechet: produit.denominationDechet
			});

			this.cleanEditMode(produit);
			await this.loadProduits();
			this.notification.success('SOCIETES.PRODUITS_ECO_ORGANISME.UPDATED');
		} catch (error) {
			this.notification.error('SOCIETES.PRODUITS_ECO_ORGANISME.UPDATE_FAILED');
		} finally {
			this.stopLoading();
		}
	}

	cancelEdit(produit) {
		if (produit._originalCodeDechet !== undefined) {
			produit.codeDechet = produit._originalCodeDechet;
			produit.denominationDechet = produit._originalDenominationDechet;
			delete produit._originalCodeDechet;
			delete produit._originalDenominationDechet;
		}
		produit.editMode = false;
	}

	async deleteProduit(produit) {
		const modalInstance = this.ModalService.confirm({
			modalTitle: 'SOCIETES.PRODUITS_ECO_ORGANISME.CONFIRM_DELETE.TITLE',
			modalMsg: this.$translate.instant('SOCIETES.PRODUITS_ECO_ORGANISME.CONFIRM_DELETE.MESSAGE', {
				code: produit.codeDechet
			}),
			headerClass: 'modal-danger'
		});

		try {
			await modalInstance.result;

			this.startLoading();
			try {
				await this.ProduitsEcoOrganismeService.deleteProduit(this.societe.entete.id, produit.id);
				await this.loadProduits();
				this.notification.success('SOCIETES.PRODUITS_ECO_ORGANISME.DELETED');
			} catch (error) {
				this.notification.error('SOCIETES.PRODUITS_ECO_ORGANISME.DELETE_FAILED');
			} finally {
				this.stopLoading();
			}
		} catch (ex) {
			// Annulation de la suppression
		}
	}

	// ============ UTILITAIRES ============

	cleanEditMode(produit) {
		delete produit._originalCodeDechet;
		delete produit._originalDenominationDechet;
		produit.editMode = false;
	}

	startLoading() {
		this.loading = true;
	}

	stopLoading() {
		this.loading = false;
	}
}
